import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const istioFeatures = [
  {
    logo_images:"../assets/icons/mesh/service-1.png",
    title: "Traffic Management",
    description:
      "Implement intelligent routing, blue/green deployments, canary releases, and fault injection with Istio’s robust traffic control primitives.",
  },
  {
    logo_images:"../assets/icons/mesh/service-2.png",
    title: "Observability",
    description:
      "Gain deep insights with distributed tracing, metrics, and logging through integrations with Prometheus, Grafana, Zipkin, and Jaeger.",
  },
  {
    logo_images:"../assets/icons/mesh/service-3.png",
    title: "Security",
    description:
      "Enforce zero-trust networking with fine-grained access control, policy enforcement, and encrypted service-to-service communication.",
  },
  {
    logo_images:"../assets/icons/mesh/service-4.svg",
    title: "mTLS",
    description:
      "Enable automatic mutual TLS (mTLS) between workloads, providing identity-based authentication and encryption without code changes.",
  },
  {
    logo_images:"../assets/icons/mesh/service-5.png",
    title: "Policy Control",
    description:
      "Apply service-level policies for quotas, rate limiting, retries, and circuit breaking to maintain resilient microservice ecosystems.",
  },
  {
    logo_images:"../assets/icons/mesh/service-6.png",
    title: "Gateways",
    description:
      "Securely expose services with Istio ingress and egress gateways, providing TLS termination, protocol translation, and advanced routing.",
  },
];

const benefits = [
  "Consistent cross-cutting controls across heterogeneous microservices",
  "Improved reliability with traffic shaping, retries, and intelligent failover",
  "End-to-end encryption and identity-based access policies without code changes",
  "Unified observability stack for tracing, metrics, and logging",
  "Rapid experimentation through canary and blue/green deployments",
  "Centralized policy management to ensure governance and compliance",
];

const expertise = [
  "Production-grade Istio deployments across EKS, AKS, and GKE",
  "Mesh expansion strategies for VMs, multi-cluster, and hybrid environments",
  "GitOps-driven configuration management with Helm, ArgoCD, and Flux",
  "Security hardening with mTLS, JWT validation, and RBAC",
  "Observability pipelines using Prometheus, Grafana, Kiali, and Jaeger",
  "Performance tuning for high-throughput, low-latency service meshes",
];

const useCases = [
  {
    title: "Global API Platform",
    description:
      "Implemented Istio traffic management and gateways to orchestrate multi-region API traffic, enabling blue/green releases with zero downtime.",
  },
  {
    title: "Regulated Healthcare SaaS",
    description:
      "Deployed Istio mTLS, policy enforcement, and auditing to meet HIPAA requirements while reducing manual security overhead by 60%.",
  },
  {
    title: "FinTech Transaction Engine",
    description:
      "Integrated Istio with Prometheus and Jaeger to gain real-time visibility, cutting mean-time-to-detect incidents from 40 minutes to under 5.",
  },
  {
    title: "Multi-Cloud Microservices",
    description:
      "Connected services across AWS and Azure via Istio multi-cluster mesh, delivering consistent security and routing policies across clouds.",
  },
];

const reasons = [
  "Certified Kubernetes and Istio specialists with multi-industry experience",
  "Battle-tested blueprints for multi-cluster and multi-cloud service meshes",
  "Security-first approach with zero-trust networking and compliance guardrails",
  "Automation-centric delivery using Helm charts, Terraform, and GitOps",
  "24/7 support and SRE services for mission-critical microservices",
  "Proven accelerators for monitoring, alerting, and performance optimization",
];

const ServiceMesh = () => {
  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Ayanshtech Solutions — Service Mesh (Istio) consulting and implementation. Traffic management, observability, security, and gateways for microservices."
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Service Mesh (Istio) | Microservices Networking | Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-service-mesh"
        />
        <meta
          property="og:description"
          content="Ayanshtech Solutions — Service Mesh (Istio) consulting and implementation. Traffic management, observability, security, and gateways for microservices."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Service Mesh (Istio) | Microservices Networking | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Ayanshtech Solutions — Service Mesh (Istio) consulting and implementation. Traffic management, observability, security, and gateways for microservices."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <link
          rel="canonical"
          href="http://ayanshtechnology.com/devops-service-mesh"
        />
        <title>
          Service Mesh (Istio) | Microservices Networking | Ayanshtech Solutions
        </title>
      </Helmet>

      <div className="flex flex-col items-center justify-center mb-6 p-6">
        {/* Hero */}
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Service Mesh (Istio)
              </h1>
              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Modern microservices demand consistent networking, security, and
                observability. Istio, the leading service mesh, delivers these
                capabilities without requiring application code changes. Ayanshtech
                designs, implements, and manages production-ready Istio service
                meshes tailored to your architecture.
              </p>
              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                From traffic management and policy enforcement to encrypted
                service-to-service communication, our experts help you adopt
                Istio with confidence—across single clusters, multi-cluster, and
                hybrid environments.
              </p>
              <button
                onClick={() => scrollToCTA()}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Talk to an Istio Expert
              </button>
            </div>
            <div className="flex w-full md:w-1/2 justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/istio-deployment.svg"
                alt="Service Mesh"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* What is Service Mesh */}
        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            What is a Service Mesh?
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            A service mesh is an infrastructure layer that handles
            service-to-service communication within microservice architectures.
            Using lightweight sidecar proxies, service meshes deliver consistent
            traffic control, security, and observability across distributed
            applications—without embedding these concerns into application code.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Istio is the most widely adopted service mesh because it provides
            powerful traffic management, zero-trust security, and deep insights
            into application behavior. It integrates seamlessly with Kubernetes
            and legacy workloads, enabling platform teams to enforce policies
            centrally while empowering developers to ship faster.
          </p>
        </div> */}

        {/* Istio Features */}
        <div className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Istio Service Mesh Features
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Enterprise-grade capabilities to secure, observe, and control
            microservices at scale.
          </p>
        </div>
        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {istioFeatures.map((feature) => (
                <div
                  key={feature.title}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                 <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={feature.logo_images} className="w-full" alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{feature.title}</h3>
                  </div>
                  <p className="text-lg text-gray-700">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Benefits for Microservices
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Unlock resilient, secure, and observable microservices with a
            service mesh architecture.
          </p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Expertise */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Our Service Mesh Expertise
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              End-to-end Istio consulting—from architecture design to managed
              operations.
            </p>
          </div>
          <div className="flex flex-col md:flex-row justify-center item-center ">
          <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-7xl my-auto space-y-3">
            {expertise.map((item) => (
              <li key={item} className="text-gray-700">
                {item}
              </li>
            ))}
          </ul>
          <div className="w-full md:w-1/2 lg:w-[40%]">
            <img src="../assets/images/devops-pages/istio-image.svg" alt="" />
          </div>
          </div>
        </div>

        {/* Use Cases */}
        <div className="text-center mb-5 mt-10">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Real-World Use Cases
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Proven implementations for regulated, high-scale, and hybrid-cloud
            environments.
          </p>
        </div>
        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {useCases.map((useCase) => (
                <div
                  key={useCase.title}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                  
                >
                  <h3 className="text-lg font-bold text-gray-900">
                    {useCase.title}
                  </h3>
                  <p className="text-lg text-gray-700">
                    {useCase.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why Choose Ayanshtech for Service Mesh
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Strategic partners for end-to-end service mesh adoption, tuned to
              your business and compliance needs.
            </p>
          </div>
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {reasons.map((reason) => (
                <div
                  key={reason}
                  className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center">
            <button
              onClick={() => scrollToCTA()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule a Service Mesh Strategy Call
            </button>
          </div>
        </div>

        {/* CTA */}
        <div id="contact-section" className="w-full">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

const scrollToCTA = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

export default ServiceMesh;

